import React from "react"
import ExchangeRateManager from "@/components/ExchangeRateManager"
import { TrendingUp, DollarSign, RefreshCw, ArrowRight, Clock, HelpCircle, Rocket } from "lucide-react"

const ExchangeRates: React.FC = () => {
  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center">
            <TrendingUp size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              Exchange Rates
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage USD to CDF exchange rates for payroll calculations
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card px-4 py-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Current Rate</div>
            <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1">
              <DollarSign size={12} className="text-indigo-600" />
              Live Updates
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card px-4 py-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Last Updated</div>
            <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1">
              <RefreshCw size={11} className="text-indigo-600" />
              Today
            </div>
          </div>
        </div>
      </div>

      {/* Exchange Rate Manager Component */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <ExchangeRateManager />
      </div>

      {/* Quick Actions Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Rocket size={12} className="text-white" />
            </div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Quick Actions</h2>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Frequently used</div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              title: "Payroll Processing",
              description: "Process salaries with current rates",
              path: "/payroll",
              gradient: "from-emerald-500 to-teal-600",
              icon: <DollarSign size={14} />,
            },
            {
              title: "Reports & Analytics",
              description: "View financial reports",
              path: "/reports",
              gradient: "from-blue-500 to-indigo-600",
              icon: <TrendingUp size={14} />,
            },
            {
              title: "System Settings",
              description: "Configure preferences",
              path: "/admin/settings",
              gradient: "from-indigo-500 to-indigo-600",
              icon: <RefreshCw size={14} />,
            },
          ].map((action, index) => (
            <div
              key={index}
              onClick={() => window.location.href = action.path}
              className="group bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 shadow-card hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`h-8 w-8 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-200 bg-gradient-to-r ${action.gradient}`}
                >
                  <div className="text-white">
                    {action.icon}
                  </div>
                </div>
                <ArrowRight
                  size={12}
                  className="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transform group-hover:translate-x-0.5 transition-all duration-200"
                />
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1 group-hover:text-gray-800 dark:group-hover:text-white transition-colors">
                {action.title}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed">{action.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Information Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-6 w-6 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
            <HelpCircle size={12} className="text-gray-600 dark:text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Need Help?</h3>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-xs mb-3">
          Exchange rates are automatically applied to payroll calculations. Update rates regularly for accurate salary processing.
        </p>
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
            onClick={() => window.location.href = "/help"}
          >
            <HelpCircle size={10} />
            Get Help
          </button>
          <button
            className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
            onClick={() => window.location.href = "/docs"}
          >
            <Clock size={10} />
            View Docs
          </button>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Rate Accuracy</div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">99.8%</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Auto Updates</div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">Enabled</div>
        </div>
      </div>
    </div>
  )
}

export default ExchangeRates